// Notification Service for Web Notifications
class NotificationService {
  constructor() {
    this.isSupported = 'Notification' in window;
  }

  // Check if notifications are supported
  get supported() {
    return this.isSupported;
  }

  // Get current permission status
  get currentPermission() {
    return this.isSupported ? Notification.permission : 'denied';
  }

  // Check if we have permission and notifications are available
  get canShow() {
    return this.isSupported && this.currentPermission === 'granted';
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      return 'denied';
    }

    if (this.currentPermission === 'granted') {
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Show a notification
  show(title, options = {}) {
    if (!this.canShow) {
      return null;
    }

    // Create absolute URL for icon
    const getIconUrl = (iconPath) => {
      return new URL(iconPath, window.location.origin).href;
    };

    // Safari-friendly icon selection
    const getSafariOptimizedIcon = () => {
      const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
      return isSafari
        ? getIconUrl('/icons/apple-touch-icon.png') // 180x180, optimized for Apple
        : getIconUrl('/icons/android-chrome-192x192.png'); // 192x192 for other browsers
    };

    const defaultOptions = {
      icon: getSafariOptimizedIcon(),
      badge: getIconUrl('/icons/favicon-32x32.png'), // Smaller badge icon
      tag: 'timist-blocks',
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // Log for debugging
      console.log('Notification created with options:', defaultOptions);
      
      // Auto-close after 5 seconds if not requiring interaction
      if (!defaultOptions.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
